import React from 'react';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import {
  REWARD_CASH, sampleTiers, tierKind, tierValue,
} from '../../lib/bonusRules';

const formatTl = (value) => `${Number(value || 0).toLocaleString('tr-TR')} ₺`;

/** Satırların kapsadığı yatırım aralıkları.
 *
 * Kademe kaydında yalnızca **alt sınır** durur; üst sınırı bir sonraki kademe
 * belirler (sunucu da öyle seçer, bkz. bonus_rules._reached_tier). Yönetici
 * "1.000 ₺ → 100 spin" yazdığında bunun 1.999 ₺ yatıranı da kapsadığını
 * tablodan göremiyor, kademe kurmak yerine tek düz adet yazıyordu; aralık bu
 * yüzden satırın altında hesaplanıp gösterilir.
 */
export const tierRanges = (tiers, exact = false) => {
  const bounds = (tiers || [])
    .map((row) => Number(row?.min_deposit))
    .filter((value) => value > 0)
    .sort((a, b) => a - b);
  return (tiers || []).map((row) => {
    const minimum = Number(row?.min_deposit);
    if (!(minimum > 0)) return '';
    if (exact) return `tam ${formatTl(minimum)}`;
    const next = bounds.find((value) => value > minimum);
    return next
      ? `${formatTl(minimum)} – ${formatTl(next - 1)}`
      : `${formatTl(minimum)} ve üzeri`;
  });
};

/** Yatırım kademeleri: "şu kadar yatırana şu kadar ödül".
 *
 * Tablo bir süre yalnızca otomatik tanımlama bölümünün içinde çiziliyor ve
 * yalnızca arka plan turunda okunuyordu: elle talep edilen kampanyada
 * görünmüyor, kaydedilse bile sessizce yok sayılıyordu. Artık iki yolda da
 * işler (bkz. routers/content_router.py → bonus talebi) ve bu yüzden ödülün
 * yanında durur — tablo, Spin Adedi / Bonus Yüzdesi alanlarını ezer.
 */
export const BonusTierTable = ({ form, setForm }) => {
  const kind = tierKind(form);
  const cash = kind === REWARD_CASH;
  const tiers = Array.isArray(form.freespin_tiers) ? form.freespin_tiers : [];
  const ranges = tierRanges(tiers, !!form.tier_exact);
  const setTiers = (next) => setForm({ ...form, freespin_tiers: next });
  const updateTier = (index, key, value) => setTiers(
    tiers.map((row, position) => (position === index ? { ...row, [key]: value } : row)),
  );
  // Satırın değeri tek bir alanda tutulur; öteki silinir ki kayıt tek türlü
  // kalsın (sunucu karışık tabloyu reddeder).
  const updateValue = (index, value) => setTiers(tiers.map((row, position) => {
    if (position !== index) return row;
    const next = { ...row, [cash ? 'amount' : 'count']: value };
    delete next[cash ? 'count' : 'amount'];
    return next;
  }));
  const sample = cash
    ? '1.000 ₺ → 100 ₺ … 10.000 ₺ → 1.000 ₺'
    : '1.000 ₺ → 100 … 10.000 ₺ → 1.000';
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">Yatırım kademeleri</span>
        <div className="flex gap-2">
          <button
            type="button"
            className="admin-chip"
            onClick={() => setTiers(sampleTiers(kind))}
          >
            <Wand2 size={11} className="inline mr-1" />
            {sample}
          </button>
          <button
            type="button"
            className="admin-chip"
            onClick={() => setTiers([...tiers, { min_deposit: '', [cash ? 'amount' : 'count']: '' }])}
          >
            <Plus size={11} className="inline mr-1" /> Kademe ekle
          </button>
        </div>
      </div>
      {tiers.length ? (
        <div className="grid gap-2" data-testid="auto-tier-table">
          {/* Sütun başlıkları: satırlar bir tablo gibi okunur, değerler
              yazıldıktan sonra da hangi sütunun ne olduğu görünür. */}
          <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <span>En az yatırım (₺)</span>
            <span>{cash ? 'Bonus tutarı (₺)' : 'Spin adedi'}</span>
            {/* Sil düğmesinin sütunu (.icon-btn 44px): başlıklar hizalı kalsın. */}
            <span className="w-11" aria-hidden="true" />
          </div>
          {tiers.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="grid grid-cols-[1fr_1fr_auto] items-start gap-2">
              <div>
                <input
                  type="number"
                  min="0"
                  value={row.min_deposit}
                  onChange={(e) => updateTier(index, 'min_deposit', e.target.value)}
                  className="admin-input"
                  placeholder="Örn. 1000"
                  aria-label={`${index + 1}. kademe yatırım tutarı`}
                />
                {ranges[index] && (
                  <span
                    className="mt-1 block px-1 text-[10px] text-slate-500"
                    data-testid="tier-range"
                  >
                    {ranges[index]}
                  </span>
                )}
              </div>
              <input
                type="number"
                min={cash ? '0.01' : '1'}
                step={cash ? '0.01' : '1'}
                value={tierValue(row, kind)}
                onChange={(e) => updateValue(index, e.target.value)}
                className="admin-input"
                placeholder="Örn. 100"
                aria-label={`${index + 1}. kademe ${cash ? 'bonus tutarı' : 'spin adedi'}`}
              />
              <button
                type="button"
                className="icon-btn ban"
                title="Kademeyi sil"
                onClick={() => setTiers(tiers.filter((_, position) => position !== index))}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] leading-relaxed text-slate-500">
          {cash
            ? 'Kademe yok — Bonus Yüzdesi ve Max. Bonus, Min. Yatırım şartıyla her uygun'
              + ' yatırıma uygulanır.'
            : 'Kademe yok — yukarıdaki Spin Adedi, Min. Yatırım şartıyla her uygun yatırıma'
              + ' verilir. "1.000 ₺ yatırana 100, 2.000 ₺ yatırana 200 spin" gibi bir kampanya'
              + ' için kademe ekleyin.'}
        </p>
      )}
      {tiers.length > 0 && (
        <label className="mt-2 flex items-start gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={!!form.tier_exact}
            onChange={(e) => setForm({ ...form, tier_exact: e.target.checked })}
            className="mt-0.5"
            aria-label="Kademe tutarı birebir eşleşmeli"
          />
          <span>
            Kademe tutarı <b>birebir</b> eşleşmeli
            <span className="block text-[11px] text-slate-500">
              Açıkken yalnızca tablodaki tutarı tam yatıran ödül alır: 1.050 ₺ ya da 10.010 ₺
              yatıran hiçbir kademeye girmez.
            </span>
          </span>
        </label>
      )}
      <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
        {form.tier_exact
          ? 'Birebir eşleşme açık: yatırım tutarı bir kademenin tutarına tam eşit olmalıdır.'
          : 'Oyuncu yatırımının ulaştığı en yüksek kademeyi alır: 7.500 ₺ yatıran 5.000 ₺'
            + ' kademesini, en üst kademenin üstünü yatıran en üst kademeyi alır.'}
        {tiers.length > 0 && (cash
          ? ' Tablo doluyken Bonus Yüzdesi ve Max. Bonus kullanılmaz.'
          : ' Tablo doluyken Spin Adedi kullanılmaz.')}
      </p>
    </div>
  );
};

export default BonusTierTable;
